import React, { useEffect, useState } from 'react';
import SmartCutoutStepParams from '@/pages/ipe/workbench/components/SmartCutoutStepParams';
import ImageEnhanceStepParams from '@/pages/ipe/workbench/components/ImageEnhanceStepParams';
import AiOutpaintStepParams from '@/pages/ipe/workbench/components/AiOutpaintStepParams';
import Img2ImgStepParams from '@/pages/ipe/workbench/components/Img2ImgStepParams';
import ImageFissionStepParams from '@/pages/ipe/workbench/components/ImageFissionStepParams';
import ModelStudioStepParams from '@/pages/ipe/workbench/components/ModelStudioStepParams';
import InfringementDetectionStepParams from '@/pages/ipe/workbench/components/InfringementDetectionStepParams';
import PodProductDesignStepParams from '@/pages/ipe/workbench/components/PodProductDesignStepParams';
import { MODEL_STUDIO_NAV_IDS } from '@/pages/ipe/workbench/constants/modelStudioConfig';
import { loadImageTemplatesForDetailBridge } from '@/pages/ipe/workbench/utils/detailToolBridgeActions';

/** 稳定的空数组：默认参数若写成 `= []`，每次渲染都是新引用，会击穿下游 useEffect 依赖导致渲染死循环。 */
const EMPTY_LIST = [];

/**
 * 详情快捷弹窗参数区：按 navId 路由到各功能 StepParams。
 */
export default function DetailToolBridgeParamsPanel({
  navId = '',
  studioParams = {},
  onStudioParamsChange,
  disabled = false,
  imageTemplates = EMPTY_LIST,
  variant = 'page',
  onImageTemplatesLoaded,
}) {
  const id = String(navId || '').trim();
  const p = studioParams && typeof studioParams === 'object' ? studioParams : {};
  const onChange = (patch) => onStudioParamsChange?.(patch);

  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [localTemplates, setLocalTemplates] = useState(
    Array.isArray(imageTemplates) ? imageTemplates : EMPTY_LIST,
  );

  useEffect(() => {
    const next = Array.isArray(imageTemplates) ? imageTemplates : EMPTY_LIST;
    // 仅在引用真的变化时更新，避免与父层形成 setState ↔ 渲染 循环
    setLocalTemplates((prev) => (prev === next ? prev : next));
  }, [imageTemplates]);

  useEffect(() => {
    if (id !== 'podProductDesign') return undefined;
    if (localTemplates.length > 0) return undefined;
    let cancelled = false;
    setCatalogsLoading(true);
    (async () => {
      try {
        const loaded = await loadImageTemplatesForDetailBridge();
        const list = Array.isArray(loaded?.templates) ? loaded.templates : [];
        if (!cancelled) {
          setLocalTemplates(list);
          onImageTemplatesLoaded?.(list);
        }
      } catch (error) {
        console.warn('加载商品模板失败', error);
      } finally {
        if (!cancelled) setCatalogsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, localTemplates.length, onImageTemplatesLoaded]);

  if (id === 'smartCutout') {
    return <SmartCutoutStepParams p={p} onChange={onChange} readOnly={disabled} />;
  }
  if (id === 'imageEnhance') {
    return <ImageEnhanceStepParams p={p} onChange={onChange} readOnly={disabled} />;
  }
  if (id === 'aiOutpaint') {
    return <AiOutpaintStepParams p={p} onChange={onChange} readOnly={disabled} />;
  }
  if (id === 'img2img') {
    return (
      <div className="detail-tool-bridge-modal__img2img-prompt">
        <Img2ImgStepParams p={p} onChange={onChange} readOnly={disabled} />
      </div>
    );
  }
  if (id === 'imageFission' || id === 'patternFission') {
    return <ImageFissionStepParams p={p} onChange={onChange} readOnly={disabled} />;
  }
  if (MODEL_STUDIO_NAV_IDS.includes(id)) {
    return <ModelStudioStepParams navId={id} p={p} onChange={onChange} readOnly={disabled} />;
  }
  if (id === 'infringementDetection') {
    return <InfringementDetectionStepParams p={p} onChange={onChange} readOnly={disabled} />;
  }
  if (id === 'podProductDesign') {
    return (
      <PodProductDesignStepParams
        p={p}
        onChange={onChange}
        imageTemplates={localTemplates}
        catalogsLoading={catalogsLoading}
        readOnly={disabled}
        showFlowIntro={variant === 'modal'}
      />
    );
  }
  return null;
}
